// ═══════════════════════════════════════════════════════════
// Hero Section — القسم الرئيسي
// Full-screen hero with poet portrait, animated quotes,
// and call-to-action buttons
// Content pulled from Supabase site_content table
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import '../styles/Hero.css';
import defaultHeroImage from '../assets/images/hero-portrait.jpg';

// Fallback quotes
const DEFAULT_QUOTES = [
  'في كلِّ حرفٍ أكتبُهُ... تسكنُ روحٌ تبحثُ عن وطن',
  'الشِّعرُ ليسَ كلاماً يُقال... بل صمتٌ يتكلّم',
  'أكتبُ لأنَّ القلبَ لا يعرفُ لغةً أخرى',
  'بينَ السطورِ تنامُ أحلامٌ لم تُولَد بعد',
];

function Hero() {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  // Content from Supabase
  const [heroImage, setHeroImage] = useState(defaultHeroImage);
  const [poetName, setPoetName] = useState('الشاعر وجدي عبد الصمد');
  const [poetTitle, setPoetTitle] = useState('شاعر لبناني معاصر');
  const [poetTitleEn, setPoetTitleEn] = useState('Wajdi Mounif Abdul Samad — Lebanese Contemporary Poet');
  const [quotes, setQuotes] = useState(DEFAULT_QUOTES);

  // Fetch site content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('site_content')
          .select('hero_image_url, poet_name, poet_title, poet_title_en, hero_quotes')
          .eq('id', 1)
          .single();

        if (error) throw error;
        if (data) {
          if (data.hero_image_url) setHeroImage(data.hero_image_url);
          if (data.poet_name) setPoetName(data.poet_name);
          if (data.poet_title) setPoetTitle(data.poet_title);
          if (data.poet_title_en) setPoetTitleEn(data.poet_title_en);
          if (data.hero_quotes && data.hero_quotes.length > 0) setQuotes(data.hero_quotes);
        }
      } catch (_) {
        // Use defaults
      }
    };

    fetchContent();

    // Listen for realtime updates
    const channel = supabase
      .channel('site_content_hero')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'site_content' }, () => {
        fetchContent();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Rotate quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setQuoteIndex(prev => (prev + 1) % quotes.length);
        setFadeIn(true);
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, [quotes]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="home">
      <div className="hero__bg-pattern" />
      <div className="hero__bg-letter hero__bg-letter--1">ق</div>
      <div className="hero__bg-letter hero__bg-letter--2">ش</div>

      <div className="hero__content">
        <div className="hero__portrait-wrapper">
          <div className="hero__portrait-ring" />
          <img
            src={heroImage}
            alt={`${poetName} — شاعر عربي معاصر`}
            className="hero__portrait"
            loading="eager"
          />
        </div>

        <h1 className="hero__name">{poetName}</h1>
        <p className="hero__title">{poetTitle}</p>
        <p className="hero__title-en">{poetTitleEn}</p>

        <div className="hero__quote-container">
          <p className={`hero__quote ${fadeIn ? 'hero__quote--visible' : 'hero__quote--hidden'}`}>
            « {quotes[quoteIndex]} »
          </p>
        </div>

        <div className="hero__buttons">
          <button className="btn-gold" onClick={() => scrollTo('poetry')}>
            اقرأ القصائد
          </button>
          <button className="btn-gold btn-gold--subtle" onClick={() => scrollTo('about')}>
            تعرّف على الشاعر
          </button>
        </div>
      </div>

      <div className="hero__scroll-indicator">
        <div className="hero__scroll-line" />
      </div>
    </section>
  );
}

export default Hero;